<template lang="html">
  <cw-service-request
    ref="serviceRequest"
    :serviceName="type"
    serviceIcon=""
    @submitted="submit"
    :requireSpecialInstructions="type === 'respot'"
  >
    <!-- :open on cw-accordion is determined by self.boxInfoExpanded. parent (self) controls cw-accordion (child).
    cw-accordion can request that it's state toggle via @update:open. But since this is such a simple
    scenario, there is no reason that self really needs to control expand/close, the @update is where all the action happens,
    and the accordion toggles itself via it's header slot soley -->
    <cw-accordion
      :height="expandedHeight"
      :showOpenIndicator="true"
      :open="boxInfoExpanded"
      @update:open="val => (boxInfoExpanded = val)"
    >
      <div slot="header">
        <h2 class="is-size-5 has-text-primary">Current Set Details</h2>
      </div>
      <cw-box-info
        id="boxInfo"
        v-if="boxSetInfo !== null"
        :boxSetInfo="boxSetInfo"
        slot="content"
      />
    </cw-accordion>
    <div class="summary-cell"></div>
    <div slot="other-cells" class="">
      <cw-service-data-cell
        :description="intendedUseForNewBox.description"
        :inputValue="intendedUseForNewBox.inputValue"
        :displayValue="selectedUseDisplay"
        :stepNumber="intendedUseForNewBox.stepNumber"
        :editing.sync="intendedUseForNewBox.editing"
        v-if="type === 'roundtrip'"
      >
        <div slot="input-control" class="control">
          <cw-dropdown
            class="selection-dropdown anchored-bottom"
            :isActive.sync="intendedUseForNewBox.editing"
          >
            <div slot="trigger">
              <p>{{ intendedUseForNewBox.inputValue }}</p>
              <p
                v-if="!intendedUseForNewBox.inputValue"
                class="has-text-grey-light"
              >
                Defaults to use of current box.
              </p>
            </div>
            <!-- duplicate dropdown in SetRequest.vue -->
            <ul v-for="use in boxUses" :key="use.value">
              <li class="dropdown-item" @click="intendedUseSelected(use.value)">
                {{ use.display }}
              </li>
            </ul>
          </cw-dropdown>
        </div>
      </cw-service-data-cell>
      <cw-service-data-cell
        :description="respotReason.description"
        :inputValue="respotReason.inputValue"
        :stepNumber="respotReason.stepNumber"
        :editing.sync="respotReason.editing"
        v-if="type === 'respot'"
        :class="{
          danger: $v.reason && $v.reason.$dirty && !$v.reason.required,
        }"
      >
        <div slot="input-control">
          <input
            class="input is-underlined"
            type="text"
            placeholder="Enter Respot Reason"
            v-model="respotReason.inputValue"
          />
        </div>
      </cw-service-data-cell>
      <cw-service-data-cell
        :description="purchaseOrder.description"
        :inputValue="purchaseOrder.inputValue"
        :stepNumber="purchaseOrder.stepNumber"
        :editing.sync="purchaseOrder.editing"
        v-if="showPOs && (!poAssigned || type === 'respot')"
        :expandedHeight="availablePOs.length > 0 ? '30vh' : undefined"
        overflowType="scroll"
        :class="{
          danger:
            $v.poNumber &&
            $v.poNumber.$dirty &&
            this.type != 'respot' &&
            !$v.poNumber.required,
        }"
      >
        <div slot="input-control">
          <input
            class="input is-underlined"
            type="text"
            placeholder="Enter Purchase Order Number"
            v-model="purchaseOrder.inputValue"
          />
          <p v-if="type === 'respot'" class="help">
            A PO may be required before the order can be filled.
          </p>
          <ul>
            <li v-for="(po, index) in purchaseOrders" :key="index">
              <cw-list-cell
                :title="po.purchaseOrderNumber"
                :showDisclosureIndicator="false"
                :showSelectedIndicator="po.purchaseOrderNumber === selectedPO"
                class="has-border-bottom"
                @click="poSelected(po)"
              >
              </cw-list-cell>
            </li>
          </ul>
        </div>
      </cw-service-data-cell>
      <p
        v-if="type === 'respot'"
        class="is-padded-small has-text-centered has-text-grey-light"
      >
        Contact dispatch if you want box moved from one lot to another; billing
        will stay with current lot.
      </p>
    </div>
  </cw-service-request>
</template>

<script>
import {
  DISPATCH_SET_DETAILS_QUERY,
  AVAILABLE_POS,
  BOX_OPTIONS,
  CREATE_DISPATCH_PULL,
  CREATE_DISPATCH_ROUNDTRIP,
  CREATE_DISPATCH_RESPOT,
} from '@/graphql/constants';
import moment from 'moment/moment';

import authRequired from '@/components/mixins/auth-required';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';

export default {
  name: 'pull-request',
  mixins: [authRequired, validationMixin],
  components: {},
  props: {
    boxId: {
      type: String,
      required: false,
      default: '',
    },
    type: {
      type: String,
      required: false,
      default: 'pull',
    },
  },
  data() {
    return {
      boxSetInfo: null,
      boxOptions: {},
      availablePOs: [],
      selectedPO: '',
      boxInfoExpanded: false,
      purchaseOrder: {
        description: 'Purchase Order',
        inputValue: '',
        stepNumber: this.type === 'pull' ? 3 : 4,
        editing: false,
      },
      intendedUseForNewBox: {
        description: 'Intended Use For New Box',
        inputValue: '',
        stepNumber: 3,
        editing: false,
      },
      respotReason: {
        description: 'Respot Reason',
        inputValue: '',
        stepNumber: 3,
        editing: false,
      },
      expandedHeight: 'auto',
    };
  },
  validations() {
    let shared = {
      poNumber: {
        isNotEmptyForRespot(value) {
          if (this.type === 'respot' && !value && this.showPOs) {
            return false;
          }
          // either this is not a respot or there is a value in the po field
          return true;
        },
      },
    };
    if (this.type === 'respot') {
      shared.reason = {
        required,
      };
    } else if (this.showPOs && !this.poAssigned) {
      // only require po number for pull and roundtrip
      shared.poNumber = {
        required,
      };
    }

    return shared;
  },
  apollo: {
    boxSetInfo: {
      query: DISPATCH_SET_DETAILS_QUERY,
      variables() {
        return { workOrderId: this.boxId };
      },
      update(data) {
        // The returned value will update
        // the vue property 'boxSetInfo'
        return data.dispatchSet;
      },
    },
    availablePOs: {
      query: AVAILABLE_POS,
      variables() {
        return { subProjectId: this.boxSetInfo.subProjectId };
      },
      update: data => data.purchaseOrdersAvailable,
      skip() {
        return (
          !this.showPOs || !this.boxSetInfo || !this.boxSetInfo.subProjectId
        );
      },
    },
    boxOptions: {
      query: BOX_OPTIONS,
      update: data => data && data.boxOptions,
    },
  },
  computed: {
    poNumber() {
      return (
        (this.boxSetInfo &&
          this.boxSetInfo.purchaseOrder &&
          this.boxSetInfo.purchaseOrder.purchaseOrderNumber) ||
        this.purchaseOrder.inputValue
      );
    },
    newUse() {
      return (
        (this.intendedUseForNewBox.inputValue.length > 0 &&
          this.intendedUseForNewBox.inputValue) ||
        (this.boxSetInfo && this.boxSetInfo.intendedUse)
      );
    },
    boxUses() {
      // Get all the possible uses from the boxOptions
      // If there are no box options, return no uses
      if (
        !this.boxSetInfo ||
        !this.boxSetInfo.box ||
        !this.boxOptions ||
        !this.boxOptions.useSizeMapping
      ) {
        return [];
      }

      // Filter the uses by those that are allowed for the box size
      return this.boxOptions.useSizeMapping
        .filter(item =>
          item.sizes.some(size => size.value === this.boxSetInfo.box.boxSize)
        )
        .map(item => item.use);
    },
    selectedOptions() {
      if (this.newUse) {
        return this.boxOptions.useSizeMapping.find(
          item => item.use.value == this.newUse
        );
      }
      return undefined;
    },
    selectedUseDisplay() {
      return (this.selectedOptions && this.selectedOptions.use.display) || '';
    },
    reason() {
      return this.respotReason.inputValue;
    },
    poAssigned() {
      return this.boxSetInfo && this.boxSetInfo.poAssigned;
    },
    showPOs() {
      return (
        this.$store.state.user.customer &&
        this.$store.state.user.customer.requiresPO
      );
    },
    regularPOs() {
      return (
        (this.availablePOs && this.availablePOs.filter(po => !po.isRespotPO)) ||
        []
      );
    },
    respotPOs() {
      return (
        (this.availablePOs && this.availablePOs.filter(po => po.isRespotPO)) ||
        []
      );
    },
    purchaseOrders() {
      if (this.type === 'respot') {
        return this.respotPOs;
      }
      return this.regularPOs;
    },
    boxSetInfoHasBoxNumber() {
      return (
        this.boxSetInfo && this.boxSetInfo.box && this.boxSetInfo.box.boxNumber
      );
    },
  },
  watch: {
    poNumber() {
      // if (this.$v.poNumber) {
      this.$v.poNumber.$touch();
      // }
    },
    reason() {
      this.$v.reason.$touch();
    },
  },
  methods: {
    intendedUseSelected(use) {
      this.intendedUseForNewBox.inputValue = use;
      this.intendedUseForNewBox.editing = false;
    },
    poSelected(po) {
      this.selectedPO = po.purchaseOrderNumber;
      this.purchaseOrder.inputValue = this.selectedPO;
    },
    submit() {
      if (!this.boxSetInfoHasBoxNumber) {
        this.$modal.show('alert', {
          title: 'No Box Number',
          message:
            'There is no box number associated with this lot. You must contact Dispatch to service this box.',
        });
        return;
      }
      // respond to any invalid entries from these cells or ServiceRequest instructions cell. Other invalid entries from ServiceRequest are handled in a lower if.
      if (
        this.$v.$invalid ||
        (this.$refs.serviceRequest.$v.instructions &&
          !this.$refs.serviceRequest.$v.instructions.required)
      ) {
        this.$v.$touch();
        this.$refs.serviceRequest.$v.$touch();

        if (
          this.type === 'respot' &&
          this.showPOs &&
          !this.$v.poNumber.isNotEmptyForRespot
        ) {
          // if submitting a respot request, is a customer that requires a po, and hasn't entered a po yet

          this.$modal.show('alert', {
            title: 'Are you sure you want to submit a respot without a PO?',
            // subtitle: '',
            message:
              'If this is a chargeable respot, it will be put on POHold until you contact Dispatch with your respot PO',
            actions: [
              {
                title: 'Ok',
                classes: ['is-primary'],
                callback: () => {
                  this.createOrder();
                  this.$modal.hide('alert');
                },
              },
              {
                title: 'Cancel',
                classes: ['is-black'],
              },
            ],
            type: 'warning',
          });
          return;
        }

        // if this is any invalid entry other than missing-po-for-respot
        this.$modal.show('alert', {
          title: 'Insufficient Info',
          message:
            'Some of the fields are invalid. Please check that you have entered the required information.',
          type: 'warning',
        });
        return;
      }

      this.createOrder();
    },
    createOrder() {
      this.$modal.show('wifi-loader', { message: 'Processing Request' });

      let instructions = this.$refs.serviceRequest.specialInstructions
        .inputValue;
      let date = this.$refs.serviceRequest.preferredBy.inputValue;
      // important to format with ISO format string
      date =
        (date && moment(date, 'MM-DD-YYYY').toJSON()) || moment().add(2, 'd');
      let time = this.$refs.serviceRequest.formattedTime;

      let mutation;

      switch (this.type) {
        case 'pull':
          mutation = CREATE_DISPATCH_PULL;
          break;
        case 'roundtrip':
          mutation = CREATE_DISPATCH_ROUNDTRIP;
          break;
        case 'respot':
          mutation = CREATE_DISPATCH_RESPOT;
          break;
        default:
          mutation = CREATE_DISPATCH_PULL;
      }

      this.$apollo
        .mutate({
          mutation: mutation,
          variables: {
            workOrderId: this.boxId,
            date,
            time,
            instructions,
            use: this.newUse,
            reason: this.respotReason.inputValue,
            ponumber: this.purchaseOrder.inputValue,
            boxNumber: this.boxSetInfo.box.boxNumber,
          },
        })
        .then(result => {
          let key = Object.keys(result.data)[0];
          let status = result.data[key];
          let success = status.code === 200;
          if (success) {
            this.$modal.hide('*');
            // Push an optimistic order to the list so it appears immediately,
            // before the SQS consumer writes to FileMaker.
            const optimisticOrder = {
              ...this.boxSetInfo,
              dispatchPull: {
                ...(this.boxSetInfo.dispatchPull || {}),
                tripType:
                  this.type.charAt(0).toUpperCase() + this.type.slice(1),
              },
              _optimisticKey: `service-${this.boxId}-${Date.now()}`,
              _optimisticAt: Date.now(),
            };
            this.$store.commit('orders/addOptimisticOrder', optimisticOrder);
          }
          this.$broadcast().$emit('order-submitted', {
            type: this.type,
            workOrderId: this.boxId,
            success,
          });
          this.$modal.hide('wifi-loader');
          this.$modal.show('alert', {
            type: success ? 'success' : 'warning',
            title: status.title,
            subtitle: success ? 'Your order has been submitted.' : '',
            message: status.message,
            actions: [
              {
                title: 'Ok',
                classes: ['is-black'],
                callback: () => {
                  if (success) {
                    this.$modal.hide('*');
                  } else {
                    this.$modal.hide('alert');
                  }
                },
              },
            ],
          });
        })
        .catch(err => {
          window.console.error(err);
          this.$modal.hide('wifi-loader');
          this.$modal.show('alert', {
            type: 'warning',
            title: 'Something Went Wrong!',
            message: 'There was an error proccessing this request',
            actions: [
              {
                title: 'Ok',
                classes: ['is-black'],
                callback: () => {
                  this.$modal.hide('*');
                },
              },
            ],
          });
        });
    },
  },
  mounted() {
    this.$apollo.queries.boxSetInfo.refetch();
  },
};
</script>

<style lang="stylus" scoped></style>
